import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@app/core/services/toast.service';
import { QuotasService } from '@app/core/services/quotas.service';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { CheckoutBundleService } from '@app/consignor/services/bundle-checkout.service';
import {
    DeliveryNoteBundlesService,
    DeliveryNotesService,
    PdfService,
} from 'api/services';
import { PdfService as PdfServiceAddons } from '../../../../api/addons/services/pdf.service';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { AlertDialogComponent } from '@app/shared/components/alert-dialog/alert-dialog.component';
import { QuestionDialogComponent } from '@app/shared/components/question-dialog/question-dialog.component';
import { QuestionCloseResult } from '@app/shared/models/components/question-close-result';
import { AddNotesToBundleDialogComponent } from '@app/consignor/components/add-notes-to-bundle-dialog/add-notes-to-bundle-dialog.component';
import { ViewBundleDialogComponent } from '@app/shared/components/view-bundle-dialog/view-bundle-dialog.component';
import { SendWalletInviteComponent } from '@app/shared/components/send-wallet-invite/send-wallet-invite.component';
import { CheckoutModalDialogComponent } from '@app/consignor/components/checkout-modal-dialog/checkout-modal-dialog.component';
import { ErrorHandler } from '@app/core/services/error-handler.service';
import { lastValueFrom } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { DeliveryNote } from '@app/shared/models/business-model/delivery-note';
import { SearchStore } from '@app/stores/searchStore';
import { ShowPdfDialogComponent } from '@app/shared/components/show-pdf-dialog/show-pdf-dialog.component';
import { EditNoteDialogComponent } from '@app/consignor/components/edit-note-dialog/edit-note-dialog.component';
import { AppStore } from '@app/stores/appStore';
import {
    DeliveryNoteWithOrgSiteKey,
    downloadBlobAsFile,
    FolderData,
} from '@app/shared/models/table-data-types';
import { DeliveryNoteData } from '@app/stores/deliveryNotesStore';
import { hasConsignorPdf } from '@app/core/stores/permissionsStore';
import { StrictHttpResponse } from '../../../../api/strict-http-response';

@Injectable({
    providedIn: 'root',
})
export class ConsignorViewActionsService {
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);
    constructor(
        private router: Router,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private toastService: ToastService,
        private quotasService: QuotasService,
        private sessionService: SessionService,
        private checkoutBundleService: CheckoutBundleService,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private deliveryNotesService: DeliveryNotesService,
        private errorHandler: ErrorHandler,
        private spinner: NgxSpinnerService,
        private pdfService: PdfService,
        private pdfServiceAddons: PdfServiceAddons
    ) {}

    /**
     * Creates a new delivery note bundle (folder)
     */
    async createNewDeliveryNoteBundle(): Promise<void> {
        const reached = await this.quotasService.checkQuotas(
            'BundlesPerMonth',
            true
        );
        if (!reached) {
            this.router.navigate([
                RoutingEndpoints.consignor +
                    '/' +
                    RoutingEndpoints.consignor_new_folder,
            ]);
        }
    }

    /**
     * Merges selected delivery note bundles
     */
    async mergeDeliveryNoteBundles(selection: FolderData[]): Promise<void> {
        if (!selection || selection.length < 2) {
            return;
        }

        const deliveryNoteBundleKeys = selection.map(d => d._key);
        const organizationSiteKey = selection[0].accessSiteKey;

        try {
            await lastValueFrom(
                this.deliveryNoteBundlesService.postMergeBundles({
                    organizationSiteKey: organizationSiteKey,
                    body: deliveryNoteBundleKeys,
                })
            );

            this.toastService.success(
                this.translateService.instant('OUTGOING-GOODS.MERGE-SUCCESS')
            );

            // Trigger data refresh to reload the table
            this.searchStore.triggerDataRefresh();
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('OUTGOING-GOODS.MERGE-ERROR')
            );
        }
    }

    /**
     * Checks out selected delivery note bundles
     */
    async checkoutDeliveryNoteBundles(selection: FolderData[]): Promise<void> {
        if (!selection || selection.length === 0) {
            return;
        }

        const noNotesBundle = selection.find(
            bundle => bundle.numberOfDeliveryNotes === 0
        );

        if (noNotesBundle) {
            this.dialog.open(AlertDialogComponent, {
                disableClose: true,
                data: {
                    message: this.translateService.instant(
                        'OUTGOING-GOODS.CHECKOUT-NO-NOTES-CONFIRMATION'
                    ),
                },
            });
        } else {
            this.checkoutBundleService.selection = selection.map(folder => ({
                ...folder,
                accessToken: '',
                orgSiteKey: folder.accessSiteKey,
                read_only: folder.read_only || false,
            })) as FolderData[];
            this.router.navigate([
                RoutingEndpoints.consignor,
                RoutingEndpoints.consignor_checkout_bundle,
            ]);
        }
    }

    /**
     * Creates a folder from selected delivery notes
     */
    async createFolderFromDeliveryNotes(
        selection: DeliveryNoteWithOrgSiteKey[]
    ): Promise<void> {
        if (!selection || selection.length === 0) {
            return;
        }

        // Check quotas first
        const reached = await this.quotasService.checkQuotas(
            'BundlesPerMonth',
            true
        );
        if (reached) {
            return;
        }

        const selectionWithLoadCarriers = selection.map(note => ({
            ...note,
            loadCarriers: [], // or set to true if needed
        }));
        this.checkoutBundleService.setNewBundleSelection(
            selectionWithLoadCarriers
        );

        // Navigate to the new folder page (following old pattern)
        setTimeout(() => {
            this.router.navigate([
                RoutingEndpoints.consignor,
                RoutingEndpoints.consignor_new_folder,
            ]);
        }, 300);
    }

    /**
     * Uploads delivery notes - switches to upload mode
     */
    async uploadDeliveryNotes(): Promise<void> {
        this.appStore.setIsUploadMode(true);
    }

    /**
     * Edits a delivery note bundle
     */
    editBundle(bundle: FolderData): void {
        this.router.navigate([
            RoutingEndpoints.consignor +
                '/' +
                RoutingEndpoints.consignor_edit_bundle +
                '/' +
                bundle._key +
                '/' +
                bundle.accessSiteKey,
        ]);
    }

    /**
     * Opens dialog to add notes to a bundle
     */
    async openAddNotesToBundleDialog(deliveryNoteBundle: FolderData): Promise<void> {
        if (
            deliveryNoteBundle.status === 'sent' ||
            deliveryNoteBundle.read_only
        ) {
            return;
        }

        const dialogRef = this.dialog.open(AddNotesToBundleDialogComponent, {
            minWidth: '800px',
            height: '90vh',
            disableClose: true,
            data: {
                deliveryNoteBundle,
                organizationSiteKey: deliveryNoteBundle.accessSiteKey,
            },
        });

        const result = await lastValueFrom(dialogRef.afterClosed());
        if (result) {
            const addedNotes = result as DeliveryNote[];
            console.log('Added notes to bundle:', addedNotes);
            // Trigger data refresh to reload the table
            this.searchStore.triggerDataRefresh();
        }
    }

    /**
     * Deletes a delivery note bundle
     */
    async deleteFolder(deliveryNoteBundle: FolderData): Promise<void> {
        this.appStore.setCloseButtonVisibility(false);
        const dialogRef = this.dialog.open(QuestionDialogComponent, {
            minWidth: '360px',
            data: {
                msg: 'CONFIRM-DELETE-DIALOG.SUBHEADLINE',
                iconClass: '',
                icon: 'delete',
                headline: 'CONFIRM-DELETE-DIALOG.HEADLINE',
                confirmText: 'CONFIRM-DELETE-DIALOG.BUTTON-DELETE',
                cancelText: 'CONFIRM-DELETE-DIALOG.BUTTON-CANCEL',
                refNumber: deliveryNoteBundle.bundleNumber,
            },
            disableClose: true,
        });

        const result: QuestionCloseResult = await lastValueFrom(dialogRef.afterClosed());
        this.appStore.setCloseButtonVisibility(true);
        if (result === QuestionCloseResult.YES) {
            try {
                await lastValueFrom(
                    this.deliveryNoteBundlesService.deleteBundle({
                        organizationSiteKey:
                            deliveryNoteBundle.accessSiteKey,
                        deliveryNoteBundleKey: deliveryNoteBundle._key,
                    })
                );

                this.toastService.success(
                    this.translateService.instant(
                        'OUTGOING-GOODS.FOLDER-VIEW.DELETE-FEEDBACK'
                    )
                );

                // Trigger data refresh to reload the table
                this.searchStore.triggerDataRefresh();
            } catch {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
            }
        }
    }

    /**
     * Shows folder details in a dialog
     */
    viewFolder(deliveryNoteBundle: FolderData): void {
        this.dialog.open(ViewBundleDialogComponent, {
            data: {
                deliveryNoteBundle,
                organizationSiteKey: deliveryNoteBundle.accessSiteKey,
                context: 'consignor',
            },
            disableClose: true,
            width: '800px',
        });
    }

    /**
     * Sends wallet invite for a bundle
     */
    sendWalletInvite(dlsDeliveryNoteBundle: FolderData): void {
        this.dialog.open(SendWalletInviteComponent, {
            minWidth: '360px',
            maxWidth: '90vw',
            disableClose: true,
            data: {
                dlsDeliveryNoteBundle,
                organizationSiteKey: dlsDeliveryNoteBundle?.accessSiteKey,
                isCarrier: false,
            },
        });
    }

    /**
     * Opens signature dialog for checkout
     */
    async openSignatureDialog(
        organizationSiteKey: string,
        deliveryNoteBundleKey: string
    ): Promise<void> {
        const data = {
            organizationSiteKey,
            deliveryNoteBundleKey,
        };
        const dialogRef = this.dialog.open(CheckoutModalDialogComponent, {
            minWidth: '360px',
            data,
            disableClose: false,
        });
        const response = await lastValueFrom(dialogRef.afterClosed());
        console.log(response);
        // Trigger data refresh to reload the table
        this.searchStore.triggerDataRefresh();
    }

    /**
     * Downloads delivery bundle as PDF
     */
    async downloadDeliveryBundle(bundle: FolderData): Promise<void> {
        console.log(bundle);
        try {
            this.spinner.show();
            const response = await lastValueFrom(
                this.deliveryNotesService.getDeliveryNotePdfsForBundle({
                    organizationSiteKey: bundle.accessSiteKey,
                    deliveryNoteBundleKey: bundle._key,
                })
            );
            const url = window.URL.createObjectURL(response);
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.href = url;
            const bundleNumber = bundle.bundleNumber;
            a.download = bundleNumber.replace('.', '-') + '.pdf';
            a.click();
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Checks if supply chain is identical for selected bundles
     */
    isSupplyChainIdentical(selection: FolderData[]): boolean {
        if (!selection || selection.length === 0) return true;

        const first = selection[0];

        if (selection.find(x => x.accessSiteKey !== first.accessSiteKey)) {
            return false;
        }

        if (selection.find(x => x.accessSiteKey !== first.accessSiteKey)) {
            return false;
        }

        if (selection.find(x => x.accessSiteKey !== first.accessSiteKey)) {
            return false;
        }

        return true;
    }

    /**
     * Checks if selection is ready for checkout
     */
    isSelectionCheckoutReady(selection: FolderData[]): boolean {
        if (selection && selection.length > 0) {
            let orgSiteKey: string = selection[0].accessSiteKey;
            const filtered = selection.filter(
                bundle => bundle.accessSiteKey !== orgSiteKey
            );
            return filtered.length === 0;
        }
        return false;
    }

    /**
     * Downloads a single delivery note PDF
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle: FolderData (optional - when delivery note is in expanded row)
     */
    async downloadDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData
    ): Promise<void> {
        this.spinner.show();
        try {
            // Use accessSiteKey from parent bundle if available (expanded row context)
            // Otherwise use the delivery note's own accessSiteKey or fall back to current org site key
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                this.appStore.currentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            let response: StrictHttpResponse<Blob>;
            if (hasConsignorPdf()) {
                response = await lastValueFrom(
                    this.pdfServiceAddons.getConsignorDeliveryNoteCombinedPdf$Response(
                        {
                            organizationSiteKey: organizationSiteKey,
                            deliveryNoteKey: deliveryNote._key,
                        }
                    )
                );
            } else {
                response = await lastValueFrom(
                    this.pdfService.getConsignorDeliveryNoteCombinedPdf$Response(
                        {
                            organizationSiteKey: organizationSiteKey,
                            deliveryNoteKey: deliveryNote._key,
                        }
                    )
                );
            }

            const filename =
                response.headers.get('content-disposition')?.split('=')[1] ||
                deliveryNote.fileName;
            downloadBlobAsFile(response.body, filename);
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Views a single delivery note in a dialog
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle: FolderData (optional - when delivery note is in expanded row)
     */
    async viewDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData
    ): Promise<void> {
        this.spinner.show();
        try {
            // Use accessSiteKey from parent bundle if available (expanded row context)
            // Otherwise use the delivery note's own accessSiteKey or fall back to current org site key
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                this.appStore.currentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            let blobData: Blob;
            if (hasConsignorPdf()) {
                blobData = await lastValueFrom(
                    this.pdfServiceAddons.getConsignorDeliveryNoteCombinedPdf({
                        organizationSiteKey: organizationSiteKey,
                        deliveryNoteKey: deliveryNote._key,
                    })
                );
            } else {
                blobData = await lastValueFrom(
                    this.pdfService.getConsignorDeliveryNoteCombinedPdf({
                        organizationSiteKey: organizationSiteKey,
                        deliveryNoteKey: deliveryNote._key,
                    })
                );
            }

            this.dialog.open(ShowPdfDialogComponent, {
                minWidth: '80%',
                height: '95%',
                disableClose: true,
                data: {
                    data: blobData,
                },
            });
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Edits a delivery note
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle: FolderData (optional - when delivery note is in expanded row)
     */
    async editDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData
    ): Promise<void> {
        // Use accessSiteKey from parent bundle if available (expanded row context)
        // Otherwise use the delivery note's own accessSiteKey or fall back to current org site key
        const organizationSiteKey =
            parentBundle?.accessSiteKey ||
            deliveryNote.accessSiteKey ||
            this.appStore.currentOrganizationSiteKey();

        const dialogRef = this.dialog.open(EditNoteDialogComponent, {
            maxWidth: 600,
            disableClose: true,
            data: {
                note: deliveryNote,
                organizationSiteKey: organizationSiteKey,
            },
        });

        const result = await lastValueFrom(dialogRef.afterClosed());
        if (result) {
            // Trigger data refresh to reload the table
            this.searchStore.triggerDataRefresh();
        }
    }

    /**
     * Deletes (unbinds) a delivery note from a bundle
     * @param deliveryNote: DeliveryNoteData
     * @param bundleKey: string
     * @param parentBundle: FolderData (optional - when delivery note is in expanded row)
     */
    async deleteDeliveryNote(
        deliveryNote: DeliveryNoteData,
        bundleKey: string,
        parentBundle?: FolderData
    ): Promise<void> {
        try {
            // Use accessSiteKey from parent bundle if available (expanded row context)
            // Otherwise use the delivery note's own accessSiteKey or fall back to current org site key
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                this.appStore.currentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            await lastValueFrom(
                this.deliveryNotesService.deleteDeliveryNoteInBundle({
                    organizationSiteKey: organizationSiteKey,
                    deliveryNoteBundleKey: bundleKey,
                    deliveryNoteKey: deliveryNote._key,
                })
            );

            this.toastService.success(
                this.translateService.instant(
                    'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.UI-FEEDBACK.DELETE-CONFIRMATION'
                )
            );

            // Trigger data refresh to reload the table
            this.searchStore.triggerDataRefresh();
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('GENERAL-ERROR')
            );
            this.errorHandler.handleError(error);
        }
    }

    /**
     * Permanently delete a delivery note (not just remove from bundle)
     */
    async deleteDeliveryNotePermanently(
        deliveryNote: DeliveryNoteData
    ): Promise<void> {
        try {
            const organizationSiteKey =
                deliveryNote.accessSiteKey || this.appStore.currentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            await lastValueFrom(
                this.deliveryNotesService.deleteDeliveryNote({
                    organizationSiteKey: organizationSiteKey,
                    deliveryNoteKey: deliveryNote._key,
                })
            );

            this.toastService.success(
                this.translateService.instant(
                    'OUTGOING-GOODS.FOLDER-VIEW.DELIVERY-NOTE-DELETED'
                )
            );

            // Trigger data refresh to reload the table
            this.searchStore.triggerDataRefresh();
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('GENERAL-ERROR')
            );
            this.errorHandler.handleError(error);
        }
    }
}
